/* HorseHalo listicle — scroll CTAs, FAQ accordion, sticky bar.
   Idempotent: safe to run again after a section re-render in the theme editor. */
(function () {
  'use strict';

  var HEADER_OFFSET = 80; // clears the sticky header

  /* ── locate the theme's own product section ────────────────────── */
  /* Found by what it is, not by where it sits, so the section stays free to be
     added, moved or removed in the theme editor without breaking the CTAs.
     Always returns the whole <section id="shopify-section-…"> wrapper. */
  function findProductSection() {
    var el =
      document.querySelector('.section-featured-product') ||
      document.querySelector('.section-main-product') ||
      document.querySelector('product-form') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('.shopify-payment-button');

    if (!el) return document.getElementById('hhlist-buybox'); // manual override

    return el.closest('[id^="shopify-section"]') || el;
  }

  /* ── smooth scroll to a named target ───────────────────────────── */
  /* Short names in the section settings map to the ids the sections render.
     Anything not in the map is treated as a raw element id, so a new target
     needs no change here. Everything falls back to the offer block. */
  var TARGET_IDS = {
    offer: 'hhlist-offer',
    reasons: 'hhlist-reasons',
    showcase: 'hhlist-showcase',
    reviews: 'hhlist-reviews'
  };

  function resolveTarget(name) {
    if (name === 'buybox') {
      return findProductSection() || document.getElementById('hhlist-offer');
    }
    var id = TARGET_IDS[name] || String(name || '').replace(/^#/, '');
    return document.getElementById(id) || document.getElementById('hhlist-offer');
  }

  function scrollToTarget(name) {
    var el = resolveTarget(name);
    if (!el) return;
    // deliberately not scrollIntoView — we need the header offset
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET,
      behavior: 'smooth'
    });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-hhlist-scroll]');
    if (!trigger) return;
    e.preventDefault();
    scrollToTarget(trigger.getAttribute('data-hhlist-scroll'));
  });

  /* ── FAQ accordion (single-open) ───────────────────────────────── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-hhlist-faq-q]');
    if (!btn) return;

    var list = btn.closest('[data-hhlist-faq]');
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    var isOpen = btn.getAttribute('aria-expanded') === 'true';

    if (list) {
      list.querySelectorAll('[data-hhlist-faq-q]').forEach(function (other) {
        if (other === btn) return;
        other.setAttribute('aria-expanded', 'false');
        var op = document.getElementById(other.getAttribute('aria-controls'));
        if (op) op.style.maxHeight = '0px';
      });
    }

    btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    if (panel) panel.style.maxHeight = isOpen ? '0px' : panel.scrollHeight + 'px';
  });

  /* ── sticky bar ────────────────────────────────────────────────── */
  function initBar() {
    var bar = document.querySelector('[data-hhlist-bar]');
    if (!bar) return;

    // reserve space at the bottom of the page so the footer is never covered
    function measure() {
      document.body.style.setProperty('--hhlist-bar-h', bar.offsetHeight + 'px');
    }
    measure();
    if (window.ResizeObserver) new ResizeObserver(measure).observe(bar);

    var triggerSel = bar.getAttribute('data-hhlist-bar-after');

    // A plain scroll listener rather than IntersectionObserver: IO only fires on
    // a change of intersection state, so a jump straight past the trigger (anchor
    // link, restored scroll position, editor preview) can skip the callback and
    // leave the bar stuck hidden. Recomputed from geometry on every scroll —
    // two getBoundingClientRect reads, cheap enough not to need rAF throttling
    // (and rAF stalls in a background tab, which would freeze the bar).
    function update() {
      var vh = window.innerHeight;

      // both targets are looked up each time, never cached: sections get
      // re-rendered and reordered in the theme editor, which would leave a
      // cached element reference pointing at a node no longer in the document
      var trigger = triggerSel ? document.querySelector(triggerSel) : null;
      var pastTrigger = true;
      if (trigger) {
        pastTrigger = trigger.getBoundingClientRect().top < vh * 0.6;
      }

      // hide the bar while the real buy box is on screen — no competing CTAs
      var buyBox = findProductSection();
      var buyBoxVisible = false;
      if (buyBox && buyBox.offsetHeight > 0) {
        var r = buyBox.getBoundingClientRect();
        buyBoxVisible = r.top < vh && r.bottom > 0;
      }

      bar.setAttribute('data-show', pastTrigger && !buyBoxVisible ? 'true' : 'false');
    }

    // bind once — init() runs again on every section re-render in the editor
    if (!bar.dataset.hhlistBound) {
      bar.dataset.hhlistBound = '1';
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });

      // update() reads geometry, and at DOMContentLoaded the geometry is a lie:
      // the hero and reason images have no height yet, so the reveal trigger
      // measures as already passed and the bar comes up over the hero. Nothing
      // corrects it either, because update() only runs on scroll — so the bar
      // stays wrongly visible until the reader scrolls. Re-run it once
      // everything has loaded, and again whenever the page height changes,
      // which is what a lazy image landing looks like from here.
      window.addEventListener('load', update);
      if (window.ResizeObserver) new ResizeObserver(update).observe(document.body);
    }
    update();
  }

  /* ── "see it in action" carousel ───────────────────────────────── */
  /* The track is a native scroll-snap container, so it already swipes and
     keyboard-scrolls with no JS. This only adds the desktop arrows and greys
     them out at the ends. */
  function initCarousels() {
    document.querySelectorAll('[data-hhlist-carousel]').forEach(function (root) {
      var track = root.querySelector('[data-hhlist-carousel-track]');
      var prev = root.querySelector('[data-hhlist-carousel-prev]');
      var next = root.querySelector('[data-hhlist-carousel-next]');
      if (!track) return;

      function step() {
        var slide = track.firstElementChild;
        if (!slide) return track.clientWidth;
        // one slide plus the gap between two of them
        var gap = 0;
        if (slide.nextElementSibling) {
          gap = slide.nextElementSibling.getBoundingClientRect().left -
                slide.getBoundingClientRect().right;
        }
        return slide.getBoundingClientRect().width + Math.max(gap, 0);
      }

      function sync() {
        // 2px of slack: fractional widths never land exactly on the end
        var atStart = track.scrollLeft <= 2;
        var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
        root.setAttribute('data-scrollable', track.scrollWidth > track.clientWidth + 2 ? 'true' : 'false');
      }

      if (!track.dataset.hhlistBound) {
        track.dataset.hhlistBound = '1';
        if (prev) prev.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
        if (next) next.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        });
        track.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync, { passive: true });
      }
      sync();
    });
  }

  function init() {
    initBar();
    initCarousels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
})();
